// src/components/TaskHeatmapCalendar.tsx
// Heatmap-style monthly calendar — TeamHub aesthetic.
// Each cell renders with an emerald intensity fill based on activity (0–5).
import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export type DayState = 'present' | 'absent' | 'leave' | 'holiday';

export interface DayDatum {
  /** ISO date "YYYY-MM-DD" */
  date: string;
  /** Activity intensity 0–5 (0 = none, 5 = max) */
  heat?: number;
  /** Optional state pill — overrides heat colour */
  state?: DayState;
  /** Show a small dot under the cell */
  hasEvent?: boolean;
}

interface Props {
  data?: DayDatum[];
  initialMonth?: Date;
  onSelectDate?: (iso: string) => void;
  legend?: boolean;
  className?: string;
  showHeader?: boolean;
}

const WEEK = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

export default function TaskHeatmapCalendar({
  data = [],
  initialMonth,
  onSelectDate,
  legend = true,
  className = '',
  showHeader = true,
}: Props) {
  const [cursor, setCursor] = useState(() => {
    const d = initialMonth || new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selected, setSelected] = useState<string | null>(null);

  const today = new Date();
  const todayISO = isoOf(today.getFullYear(), today.getMonth(), today.getDate());

  const dataMap = useMemo(() => {
    const map: Record<string, DayDatum> = {};
    for (const d of data) map[d.date] = d;
    return map;
  }, [data]);

  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const last = new Date(cursor.y, cursor.m + 1, 0);
    // Monday-first offset
    const startWd = (first.getDay() + 6) % 7;
    const result: Array<{ day: number | null; iso?: string }> = [];
    for (let i = 0; i < startWd; i++) result.push({ day: null });
    for (let d = 1; d <= last.getDate(); d++) {
      result.push({ day: d, iso: isoOf(cursor.y, cursor.m, d) });
    }
    return result;
  }, [cursor]);

  const change = (delta: number) => {
    const nd = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: nd.getFullYear(), m: nd.getMonth() });
  };

  const handleClick = (iso?: string) => {
    if (!iso) return;
    setSelected(iso);
    onSelectDate?.(iso);
  };

  return (
    <div className={`gt-card p-4 ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="gt-eyebrow">i. Activity</span>
            <h3 className="gt-display gt-numerals" style={{ fontSize: 16, marginTop: 2 }}>
              {MONTHS[cursor.m]} <em style={{ fontStyle: 'normal', color: 'var(--gt-primary-deep)' }}>{cursor.y}</em>
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => change(-1)} className="gt-icon-btn is-sm" aria-label="Previous month">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => change(1)} className="gt-icon-btn is-sm" aria-label="Next month">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {WEEK.map((w, i) => (
          <div key={i} className="gt-cal-weekday" style={{ width: '100%' }}>{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((c, i) => {
          if (c.day === null) return <div key={i} style={{ width: '100%', aspectRatio: '1' }} />;
          const datum = c.iso ? dataMap[c.iso] : undefined;
          const isToday = c.iso === todayISO;
          const isSelected = c.iso === selected;
          const heat = datum?.heat ?? 0;
          return (
            <button
              key={i}
              type="button"
              onClick={() => handleClick(c.iso)}
              className={`gt-cal-day gt-numerals ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''} ${datum?.hasEvent ? 'has-event' : ''}`}
              data-heat={heat}
              data-state={datum?.state}
              style={{ width: '100%', aspectRatio: '1', height: 'auto' }}
              aria-label={c.iso}
            >
              {c.day}
            </button>
          );
        })}
      </div>

      {legend && (
        <div className="gt-cal-legend mt-4">
          <span className="gt-cal-legend-item">
            <span className="dot" style={{ background: 'var(--gt-heat-4)' }} /> Present
          </span>
          <span className="gt-cal-legend-item">
            <span className="dot" style={{ background: '#fcd34d' }} /> Leave
          </span>
          <span className="gt-cal-legend-item">
            <span className="dot" style={{ background: '#fca5a5' }} /> Absent
          </span>
          <span className="gt-cal-legend-item">
            <span className="dot" style={{ background: '#c4b5fd' }} /> Holiday
          </span>
        </div>
      )}
    </div>
  );
}
